import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { PolymorphicHTMLProps } from '@findigs/helpers/src/helpers/polymorphic';
import { useNodeRefAndForwardIt } from '@findigs/helpers/src/hooks/use-node-ref-and-forward-it';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styles } from './link.module.scss';

export type LinkProps = PolymorphicHTMLProps & {
  /** @default false */
  inverse?: boolean;
  disabled?: boolean;
};

const durationInSeconds = 0.7;
const delayInSeconds = 0.2;

const options: KeyframeAnimationOptions = {
  duration: durationInSeconds * 1000,
  iterations: 1,
  easing: 'cubic-bezier(0.19, 1, 0.22, 1)',
};

const keyframesIn: Keyframe[] = [
  {
    backgroundSize: '0 var(--underlineWidth), 0 var(--underlineWidth)',
    backgrounPosition: '0 100%, 0 100%',
    offset: 0,
  },
  {
    backgroundSize: '0 var(--underlineWidth), 100% var(--underlineWidth)',
    backgroundPosition: '100% 100%, 0 100%',
    offset: 1,
  },
];
const keyframesOut: Keyframe[] = [
  {
    backgroundSize: '0 var(--underlineWidth), 100% var(--underlineWidth)',
    backgroundPosition: '0 100%, 100% 100%',
    offset: 0,
  },
  {
    backgroundSize: '0 var(--underlineWidth), 0 var(--underlineWidth)',
    backgroundPosition: '0 100%, 100% 100%',
    offset: 1,
  },
];

type Animations = {
  normalIn: Animation | null;
  normalOut: Animation | null;
  inverseIn: Animation | null;
  inverseOut: Animation | null;
};

// scroll to the bottom of the file to see the export
const _Link = polymorphic(
  { element: 'a', forwardRef: true },
  (
    {
      inverse = false,
      className,
      onMouseEnter: forwardedMouseEnter,
      onMouseLeave: forwardedMouseLeave,
      onMouseUp: forwardedMouseUp,
      ...props
    }: LinkProps,
    Component,
    forwardedRef
  ) => {
    const [scriptLoaded, setScriptLoaded] = useState(false);

    const [callbackRef, ref] =
      useNodeRefAndForwardIt<HTMLAnchorElement>(forwardedRef);

    const hover = useRef<boolean>(false);
    const lastPropIncludedInverse = useRef<boolean>(
      (inverse || className?.split(' ').includes('inverse')) ?? false
    );

    const animations = useRef<Animations>({
      normalIn: null,
      normalOut: null,
      inverseIn: null,
      inverseOut: null,
    });

    const getCurrentMode = useCallback(
      () =>
        ref.current?.className?.split(' ').includes('inverse')
          ? 'inverse'
          : 'normal',
      []
    );

    const onAnimationEnd = useCallback(
      (
        ran: { value: boolean },
        animationIn: Animation | null,
        animationOut: Animation | null,
        wasPlaying: 'in' | 'out' | null
      ) => {
        /* istanbul ignore next [ignoring this because it's a browser-specific fix] */
        if (ran.value) {
          return;
        }
        ran.value = true;

        let nextAnimation: Animation | null = null;

        if (hover.current && wasPlaying !== 'in') {
          nextAnimation = animationIn;
        } else if (!hover.current && wasPlaying !== 'out') {
          nextAnimation = animationOut;
        }

        if (nextAnimation) {
          for (const animation of [animationIn, animationOut]) {
            if (animation) {
              animation.onfinish = null;
              animation.finish();
              animation.cancel();
            }
          }

          nextAnimation.play();
        }
      },
      []
    );

    const onHoverChanged = useCallback(() => {
      const mode = getCurrentMode();
      const keyIn = (mode + 'In') as keyof Animations;
      const keyOut = (mode + 'Out') as keyof Animations;
      const animationIn = animations.current[keyIn];
      const animationOut = animations.current[keyOut];

      const ran = { value: false };

      const playing =
        animationIn?.playState === 'running'
          ? 'in'
          : animationOut?.playState === 'running'
          ? 'out'
          : null;

      if (playing) {
        const cb = () =>
          onAnimationEnd(ran, animationIn, animationOut, playing);
        if (animationIn) {
          animationIn.onfinish = cb;
        }
        if (animationOut) {
          animationOut.onfinish = cb;
        }
      } else {
        onAnimationEnd(ran, animationIn, animationOut, null);
      }
    }, [getCurrentMode, onAnimationEnd]);

    const onHoverInside = useCallback(() => {
      if (!hover.current) {
        hover.current = true;
        onHoverChanged();
      }
    }, [onHoverChanged]);

    const onHoverOutside = useCallback(() => {
      if (hover.current) {
        hover.current = false;
        onHoverChanged();
      }
    }, [onHoverChanged]);

    useEffect(() => {
      setScriptLoaded(true);

      // init in and out animations for each mode
      for (const mode of ['normal', 'inverse']) {
        const keyIn = (mode + 'In') as keyof Animations;
        const keyOut = (mode + 'Out') as keyof Animations;

        animations.current[keyOut] =
          ref.current?.animate(
            mode === 'inverse' ? keyframesIn : keyframesOut,
            {
              ...options,
              fill: 'backwards',
              delay: delayInSeconds * 1000,
            }
          ) ?? /* istanbul ignore next */ null;
        animations.current[keyOut]?.cancel();

        animations.current[keyIn] =
          ref.current?.animate(
            mode === 'inverse' ? keyframesOut : keyframesIn,
            {
              ...options,
              fill: 'forwards',
            }
          ) ?? /* istanbul ignore next */ null;
        animations.current[keyIn]?.cancel();
      }

      // make sure all browsers behave the same (esp. safari)
      const timer = setTimeout(() => {
        /* istanbul ignore next [ignoring because it's browser-specific fix] */
        if (ref.current?.matches(':hover')) {
          onHoverInside();
        }
      }, 5);
      return () => clearTimeout(timer);
    }, [onHoverInside]);

    // detects when the className prop changes and resets the animation
    // to avoid misbehaviors
    useEffect(() => {
      const lastIncluded = lastPropIncludedInverse.current;
      const includes =
        (inverse || className?.split(' ').includes('inverse')) ?? false;
      lastPropIncludedInverse.current = includes;
      const changed = lastIncluded !== includes;

      if (changed && animations.current) {
        let key: keyof typeof animations.current;
        for (key in animations.current) {
          const animation = animations.current[key];
          if (animation) {
            animation.onfinish = null;
            animation.finish();
            animation.cancel();
          }
        }
        onHoverOutside();
      }
    }, [className, inverse, onHoverOutside]);

    // subscribe to browser's native events to support manually
    // dispatched events.
    // this should be done for all events this component uses
    useEffect(() => {
      const node = ref.current;
      node?.addEventListener('mouseenter', onHoverInside);
      node?.addEventListener('mouseleave', onHoverOutside);
      node?.addEventListener('mouseup', onHoverOutside);
      return () => {
        node?.removeEventListener('mouseenter', onHoverInside);
        node?.removeEventListener('mouseleave', onHoverOutside);
        node?.removeEventListener('mouseup', onHoverOutside);
      };
    }, [onHoverOutside, onHoverInside]);

    const onMouseEnter = useCallback(
      (e: React.MouseEvent<any, MouseEvent>) => {
        if (forwardedMouseEnter) forwardedMouseEnter(e);
        onHoverInside();
      },
      [forwardedMouseEnter, onHoverInside]
    );

    const onMouseLeave = useCallback(
      (e: React.MouseEvent<any, MouseEvent>) => {
        if (forwardedMouseLeave) forwardedMouseLeave(e);
        onHoverOutside();
      },
      [forwardedMouseLeave, onHoverOutside]
    );

    const onMouseUp = useCallback(
      (e: React.MouseEvent<any, MouseEvent>) => {
        if (forwardedMouseUp) forwardedMouseUp(e);
        onHoverOutside();
      },
      [forwardedMouseUp, onHoverOutside]
    );

    const classes = classList(styles, { js: scriptLoaded, inverse }, className);
    return (
      <Component
        {...props}
        className={classes}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        aria-disabled={(props['aria-disabled'] ?? props.disabled) || undefined}
        ref={callbackRef}
      />
    );
  }
);

/**
 * Extends the vanilla `anchor` element (eg. `<a>`) with our styling.
 * All vanilla `anchor` props are forwarded.
 *
 * This component is [polymorphic](/docs/component-system-polymorphic-components--docs).
 */
export const Link = React.memo(_Link) as typeof _Link;

// we're using React.memo here to avoid animation glitches
